import { faker } from '@faker-js/faker/locale/en'
import startCase from 'lodash-es/startCase'
import { DateTime } from 'luxon'
import type {
  ContainerType,
  GETAppointment,
  LinkedAppointment,
  PortalName,
  Shift,
  SimpleUser,
} from '~/services/apiClient'
import {
  getPortalLabel,
  getTerminalLabel,
  TERMINALS_REQUIRING_APPOINTMENT_CHECK_IN,
  TERMINALS_REQUIRING_APPOINTMENT_CHECK_IN_FOR_INBOUND_APPOINTMENTS,
  TERMINALS_REQUIRING_LICENSE_PLATE_FOR_EMPTY_APPOINTMENT,
  TERMINALS_REQUIRING_LICENSE_PLATE_TO_COMPLETE_IMPORT_APPOINTMENT,
} from '~/constants'
import {
  shiftFromTime,
  shiftNameFromTime,
  sortableShiftKey,
} from '~/logic/shifts'
import { exportPickupWindowDescription } from '~/logic/exportPickupWindow'
import {
  AppointmentStatus,
  ChassisType,
  LicensePlateOrigin,
  TerminalName,
  TransactionDirection,
} from '~/services/apiClient'
import { fastParseISO, moveDescription } from '~/utils'

// Move Type enum
export enum MoveType {
  LoadOut = 'Load Out',
  LoadIn = 'Load In',
  EmptyOut = 'Empty Out',
  EmptyIn = 'Empty In',
}

export interface IssueBadgeInfo {
  message: string
  type: 'info' | 'warning' | 'danger' | 'light-purple'
  tooltip?: string
  link?: string
  isTag?: boolean
  issueType?:
    | 'hasHolds'
    | 'notAvailableForPickup'
    | 'missingLicensePlate'
    | 'violatesReturnRules'
    | 'requiresCheckIn'
    | 'licensePlateConflict'
    | 'needsLFDCheck'
}
/**
 * System-assigned plates that should be surfaced to the user as "placeholder" —
 * they did not choose this specific plate and may want to edit it before the gate.
 */
export function isPlaceholderPlate(
  origin: LicensePlateOrigin | undefined
): boolean {
  return (
    origin === LicensePlateOrigin.AutoBookRandom ||
    origin === LicensePlateOrigin.ManualRandom ||
    origin === LicensePlateOrigin.AutoSetWorkflow
  )
}

export class Appointment {
  id: number
  container_number: string
  /**
   * The parsed type of the box this appointment draws — set even before a
   * containerless pickup has a container number. Null on appointments recorded
   * before the backend carried it (DEV-7981); treat that as unknown, never as a
   * default size.
   */
  container_type: ContainerType | null
  terminal: TerminalName
  status: AppointmentStatus
  truck_license_plate_number?: string
  license_plate_origin?: LicensePlateOrigin
  chassis_number?: string
  chassis_type?: ChassisType
  truck_id: number | null
  terminal_block?: string
  window_start: DateTime
  window_end?: DateTime
  created_at_terminal?: DateTime
  last_observed_changed: DateTime
  last_observed: DateTime | null = null
  terminal_reference: string
  raw_display_terminal_reference: string | null
  gate_code: string | null
  linked_appointments: LinkedAppointment[]
  extra_data: object | undefined
  direction: TransactionDirection
  loaded: boolean
  requested_by: SimpleUser | null
  rescheduled_by: SimpleUser | null
  empty_rescheduled_by: SimpleUser | null
  source: PortalName
  moveType: MoveType
  scheduledShift: string
  shift: Shift
  shiftDate: DateTime
  isScheduled: boolean
  isContainerless: boolean
  assigned_users: SimpleUser[]
  /**
   * User tags on this appointment. Same org list as container tags.
   *
   * Empty when none. A row that already has a container number shows the
   * container's tags (the list response copies them onto the appointment).
   * A containerless row shows the tags stored on the appointment itself.
   */
  tags: string[]

  constructor(appt: GETAppointment, demoMode: boolean) {
    this.id = appt.id
    // container_number is nullable in the API for containerless appointments
    // (export empty pickups; see DEV-7283) — the booking number is the only
    // stable identifier until the terminal assigns one. We keep the non-null
    // contract so container-scoped consumers are unaffected, and record the
    // distinction here: '' alone can't separate "not assigned yet" from a
    // malformed payload, and export surfaces need to render a pending state.
    this.isContainerless = appt.container_number == null
    this.container_number = appt.container_number ?? ''
    this.container_type = appt.container_type ?? null
    this.terminal = appt.terminal
    this.status = appt.status
    this.truck_license_plate_number = appt.truck_license_plate_number
    this.license_plate_origin = appt.license_plate_origin
    this.chassis_number = appt.chassis_number
    this.chassis_type = appt.chassis_type
    this.truck_id = appt.truck_id ?? null
    if (this.truck_license_plate_number && demoMode) {
      this.truck_license_plate_number = faker.vehicle.vin().slice(0, 7)
    }
    this.terminal_block = appt.terminal_block
    this.window_start = fastParseISO(appt.window_start)
    this.window_end = appt.window_end
      ? fastParseISO(appt.window_end)
      : undefined
    this.linked_appointments = appt.linked_appointments || []
    this.created_at_terminal = appt.created_at_terminal
      ? fastParseISO(appt.created_at_terminal)
      : undefined
    this.last_observed_changed = fastParseISO(appt.observed)
    if (appt.last_observed) {
      this.last_observed = fastParseISO(appt.last_observed)
    }
    this.terminal_reference = appt.terminal_reference
    this.raw_display_terminal_reference =
      appt.display_terminal_reference || null
    this.gate_code = appt.gate_code || null
    this.direction = appt.direction
    this.loaded = appt.loaded
    this.extra_data = appt.extra_data
    this.requested_by = appt.requested_by || null
    this.rescheduled_by = appt.rescheduled_by || null
    this.empty_rescheduled_by = appt.empty_rescheduled_by || null
    this.source = appt.source
    this.moveType = calcMoveType(this.direction, this.loaded)
    this.scheduledShift = shiftNameFromTime(this.window_start, this.window_end)
    const [shiftDate, shift] = shiftFromTime(this.window_start)
    this.shift = shift
    this.shiftDate = shiftDate
    this.isScheduled = appt.status === AppointmentStatus.Scheduled
    this.assigned_users = appt.assigned_users ?? []
    // The generated client predates this field. The list still sends it.
    this.tags = (appt as GETAppointment & { tags?: string[] | null }).tags ?? []
  }

  /**
   * Check if this is an LBCT exempt appointment (container numbers ending in "1")
   * @returns true if this is an exempt appointment at LBCT, false otherwise
   */
  get is_exempt(): boolean {
    // LBCT exempt appointments: container numbers ending in "1"
    return (
      this.terminal === TerminalName.Lbct &&
      this.container_number?.endsWith('1') === true
    )
  }

  get isStale(): boolean {
    if (this.last_observed) {
      return this.last_observed < DateTime.now().minus({ hours: 2 })
    } else {
      return false
    }
  }

  get isInProgress(): boolean {
    return this.status === AppointmentStatus.InProgress
  }

  get display_terminal_reference(): string {
    if (this.gate_code) {
      return this.gate_code
    }
    if (this.raw_display_terminal_reference) {
      return this.raw_display_terminal_reference
    }
    return this.terminal_reference.replace(/_/, ' / ')
  }

  get booked_on_dray_dog(): boolean {
    return Boolean(
      this.requested_by || this.rescheduled_by || this.empty_rescheduled_by
    )
  }

  get booked_by(): string | null {
    const bookedBy =
      this.requested_by || this.rescheduled_by || this.empty_rescheduled_by
    if (bookedBy !== null) {
      if (bookedBy.first_name === 'Dray Dog') {
        return null
      }
      const lastName = bookedBy.last_name
      if (lastName) {
        const lastInitial = `${lastName.charAt(0)}.`
        return `${bookedBy.first_name} ${lastInitial}`
      } else {
        return `${bookedBy.first_name}`
      }
    } else {
      return null
    }
  }

  get terminal_name() {
    return getTerminalLabel(this.terminal)
  }

  get portal_name() {
    return getPortalLabel(this.source)
  }

  get window_date() {
    return this.window_start.toFormat('ccc, LLL d')
  }

  get window_start_description(): string {
    return windowTimeDesc(this.window_start)
  }

  get window_end_description(): string | null {
    if (this.window_end) {
      return windowTimeDesc(this.window_end)
    }
    return null
  }

  get is_placeholder_plate(): boolean {
    return (
      Boolean(this.truck_license_plate_number) &&
      isPlaceholderPlate(this.license_plate_origin)
    )
  }

  get missing_required_license_plate(): boolean {
    if (this.truck_license_plate_number) {
      return false
    }
    let requiresPlate = false
    if (this.is_import_out) {
      requiresPlate =
        TERMINALS_REQUIRING_LICENSE_PLATE_TO_COMPLETE_IMPORT_APPOINTMENT.has(
          this.terminal
        )
    } else if (this.is_empty_in) {
      requiresPlate =
        TERMINALS_REQUIRING_LICENSE_PLATE_FOR_EMPTY_APPOINTMENT.has(
          this.terminal
        )
    }
    return requiresPlate
  }

  get window_description(): string {
    if (this.window_end) {
      return `${this.window_start_description} - ${this.window_end_description}`
    }
    // A containerless booking is pre-advice: the terminal grants the whole day
    // rather than a window, so a lone start time reads as a deadline it isn't.
    // Only export empty pickups are containerless, so imports and empties keep
    // showing the bare start they always have. Goes through the same helper as
    // the confirm dialog so the slot picked and the appointment returned read
    // identically, date included.
    if (this.isContainerless)
      return exportPickupWindowDescription(this.window_start, this.window_end)
    return this.window_start_description
  }

  get created_at_terminal_fmtd(): string {
    if (this.created_at_terminal)
      return this.created_at_terminal.toFormat('M/d H:mm')
    else return ''
  }

  get last_observed_changed_fmtd(): string {
    return this.last_observed_changed.toLocaleString(DateTime.DATETIME_SHORT)
  }

  get type(): string {
    return moveDescription(this.direction, this.loaded)
  }

  get terminal_label(): string {
    if (this.terminal) {
      return getTerminalLabel(this.terminal)
    } else {
      return '-'
    }
  }

  get own_chassis_description(): string | null {
    if (this.terminal !== TerminalName.Apm) return null
    if (this.own_chassis === undefined) return null
    return this.own_chassis ? 'Own Chassis' : 'Pool Chassis'
  }

  /**
   * Own-vs-pool chassis choice recorded on the appointment, or undefined when we
   * never captured one.
   *
   * Prefers the dedicated `chassis_type` field when set. Falls back to legacy
   * `extra_data.own_chassis` parsing for appointments observed before that field
   * existed, mirrors the backend's two-tier `Appointment.own_chassis`
   * (`backend/dd/models/appointments.py`); the same stored value must resolve
   * the same way on both sides, since the backend uses it to inherit the choice
   * on reschedule while the frontend uses it to prefill the toggle.
   */
  get own_chassis(): boolean | undefined {
    if (this.chassis_type === ChassisType.Own) return true
    if (this.chassis_type === ChassisType.Pool) return false
    if (!this.extra_data || !('own_chassis' in this.extra_data)) {
      return undefined
    }
    const value = (this.extra_data as { own_chassis?: unknown }).own_chassis
    // Key present but null → unrecorded, not "pool" (Boolean(null) is false).
    if (value === null || value === undefined) {
      return undefined
    }
    if (typeof value === 'boolean') {
      return value
    }
    // Stored inconsistently in prod: a real boolean OR the terminal's raw string
    // (eModal writes "No"; the string form is the majority of rows). A plain
    // Boolean() cast is wrong here — Boolean("No") is true — so normalize
    // explicitly, and treat anything unrecognized as unknown rather than "pool".
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (['', 'no', 'false', 'n'].includes(normalized)) return false
      if (['yes', 'true', 'y'].includes(normalized)) return true
      return undefined
    }
    return Boolean(value)
  }

  /**
   * Green/EV status the terminal reported for this appointment, or `undefined` when it
   * didn't report one.
   *
   * Mirrors the backend's `appointment_is_green` (`dd/logic/green_trucks.py`), minus
   * its `raw_data.GreenTruck` fallback — `raw_data` is only populated when
   * `include_raw_data` is requested, so it isn't there on a normal fetch.
   *
   * Unknown is the *majority* case, not an edge case: only APM's legacy portal reports
   * the flag, and roughly three quarters of APM appointments come through the API
   * portal, which never does. Callers must treat `undefined` as "can't tell" and fall
   * back to the truck's own status rather than concluding "not green".
   */
  get is_green_truck(): boolean | undefined {
    if (!this.extra_data || !('green_truck' in this.extra_data)) {
      return undefined
    }
    const value = (this.extra_data as { green_truck?: unknown }).green_truck
    // Key present but null → the portal didn't report, which is not "not green".
    if (value === null || value === undefined) {
      return undefined
    }
    return Boolean(value)
  }

  get check_in_status(): boolean | null {
    // will be null if not applicable
    if (this.requires_check_in && this.extra_data) {
      if ('checked_in' in this.extra_data) {
        return this.extra_data.checked_in as boolean | null
      } else {
        return null
      }
    } else {
      return null
    }
  }

  get status_label(): string {
    return startCase(this.status)
  }

  get is_empty_in(): boolean {
    return this.moveType === MoveType.EmptyIn && this.loaded === false
  }

  get is_inbound(): boolean {
    return this.direction === TransactionDirection.Inbound
  }

  get requires_check_in(): boolean {
    return (
      TERMINALS_REQUIRING_APPOINTMENT_CHECK_IN.has(this.terminal) ||
      (this.is_inbound &&
        TERMINALS_REQUIRING_APPOINTMENT_CHECK_IN_FOR_INBOUND_APPOINTMENTS.has(
          this.terminal
        ))
    )
  }

  get is_dual(): boolean {
    return this.linked_appointments.length > 0
  }

  get is_import_out(): boolean {
    return this.moveType === MoveType.LoadOut && this.loaded === true
  }

  get sortable_shift_key() {
    return sortableShiftKey(this.shiftDate, this.shift)
  }
}

function windowTimeDesc(dt: DateTime): string {
  return dt.toLocaleString({
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
}

function calcMoveType(
  direction: TransactionDirection,
  loaded: boolean
): MoveType {
  if (direction === TransactionDirection.Inbound) {
    return loaded ? MoveType.LoadIn : MoveType.EmptyIn
  } else {
    return loaded ? MoveType.LoadOut : MoveType.EmptyOut
  }
}
